/**
 * What Coinbase's payment page says about itself, as the client acts on it.
 *
 * The page posts one-directional events; nothing answers them. Their polling
 * reaches the transfer before ours does, so its verdict is read here rather
 * than waited on; everything else past `PAID` arrives as progress.
 */
export type CoinbaseOnrampEvent =
  /** The pay button is drawn and can be pressed. */
  | { kind: 'READY' }
  /** The user paid and the transaction started. */
  | { kind: 'PAID' }
  /** Their polling saw the crypto sent. Terminal, and good. */
  | { kind: 'SUCCEEDED' }
  /** The user dismissed the payment sheet; the button is still there. */
  | { kind: 'CANCELLED' }
  /** Terminal for this page: the code is Coinbase's, and localized. */
  | { kind: 'FAILED'; code: string; message?: string }
  /** Named for the log, acted on by nothing. */
  | { kind: 'PROGRESS'; name: string }

const READY = 'onramp_api.load_success'
const PAID = 'onramp_api.commit_success'
const CANCELLED = 'onramp_api.cancel'
// Coinbase has renamed this one before. Every spelling seen so far is
// accepted; an unrecognised one still lands in `PROGRESS`, which logs its
// name — that log is how the next rename gets noticed.
const SUCCEEDED: readonly string[] = [
  'onramp_api.polling_success',
  'onramp_api.success',
  'onramp_api.transaction_success',
]
const FAILURES: readonly string[] = [
  'onramp_api.load_error',
  'onramp_api.commit_error',
  'onramp_api.polling_error',
  'onramp_api.session_error',
]

/**
 * Reads one posted message, or `undefined` for anything this page did not
 * send — a WebView's message channel is shared with whatever the page loads.
 */
export function readOnrampEvent(raw: string): CoinbaseOnrampEvent | undefined {
  const posted = parse(raw)
  if (posted === undefined) {
    return undefined
  }
  const { eventName, data } = posted
  if (typeof eventName !== 'string' || !eventName.startsWith('onramp_api.')) {
    return undefined
  }
  if (eventName === READY) {
    return { kind: 'READY' }
  }
  if (eventName === PAID) {
    return { kind: 'PAID' }
  }
  if (eventName === CANCELLED) {
    return { kind: 'CANCELLED' }
  }
  if (SUCCEEDED.includes(eventName)) {
    return { kind: 'SUCCEEDED' }
  }
  if (FAILURES.includes(eventName)) {
    return { kind: 'FAILED', ...failure(data) }
  }
  return { kind: 'PROGRESS', name: eventName }
}

function failure(data: unknown): { code: string; message?: string } {
  if (data === null || typeof data !== 'object') {
    return { code: 'unstated' }
  }
  const { errorCode, errorMessage } = data as Record<string, unknown>
  return {
    code: typeof errorCode === 'string' ? errorCode : 'unstated',
    // Coinbase localizes this one for the user, so it is worth carrying.
    ...(typeof errorMessage === 'string' ? { message: errorMessage } : {}),
  }
}

function parse(raw: string): Record<string, unknown> | undefined {
  try {
    const posted: unknown = JSON.parse(raw)
    return posted === null || typeof posted !== 'object'
      ? undefined
      : (posted as Record<string, unknown>)
  } catch {
    return undefined
  }
}
